import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './AdminBase.css';
import './AdminApp.css';
import { AdminFunnel } from './components/AdminFunnel';
import { AdminBooking } from './components/AdminBooking';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_LABELS = {
  appartement: 'Appartement',
  maison: 'Maison',
  immeuble: 'Immeuble en gestion',
  immeuble_collectif: 'Immeuble collectif',
};

const formatEuro = (n) =>
  `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0)}\u00A0€`;

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const AdminLogin = ({ onLogin }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/api/admin/login`, { code });
      onLogin(res.data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Connexion impossible');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="eb-main" style={{ gridTemplateColumns: '1fr', maxWidth: 480 }}>
      <form className="eb-card" onSubmit={submit} data-testid="admin-login-card">
        <h2>Accès interne</h2>
        <p className="eb-lede">Espace réservé à l'équipe EB ENERGIE.</p>
        <div className="eb-field">
          <label className="eb-label" htmlFor="admin-code">Code d'accès</label>
          <input id="admin-code" data-testid="admin-code-input" className="eb-input" type="password"
            value={code} onChange={(e) => { setCode(e.target.value); setError(null); }} placeholder="••••••••" autoFocus />
          {error && <p className="eb-error" data-testid="admin-login-error">{error}</p>}
        </div>
        <button type="submit" className="eb-btn-primary" disabled={loading || !code} data-testid="admin-login-btn">
          {loading ? 'Vérification…' : 'Entrer'}
        </button>
      </form>
    </main>
  );
};

const StatCard = ({ label, value, testid }) => (
  <div className="eb-stat-card" data-testid={testid}>
    <span className="eb-stat-value">{value}</span>
    <span className="eb-stat-label">{label}</span>
  </div>
);

const QuoteRow = ({ quote, onRemind, reminding, checked, onToggle, onOpenDetails, onInvoice, invoicing, onDownloadInvoice }) => (
  <tr data-testid={`quote-row-${quote.id}`} className={checked ? 'row-selected' : ''}>
    <td className="check-cell">
      <input type="checkbox" className="eb-checkbox" checked={checked} onChange={() => onToggle(quote.key)}
        data-testid={`select-${quote.id}`} aria-label={`Sélectionner ${quote.id}`} />
    </td>
    <td className="mono">{quote.id}</td>
    <td>{formatDate(quote.created_at)}</td>
    <td className="col-client">
      <div className="eb-client">{quote.first_name} {quote.last_name}</div>
      <div className="eb-client-sub">{quote.email}{quote.phone ? ` · ${quote.phone}` : ''}</div>
    </td>
    <td className="col-bien">
      {PROPERTY_LABELS[quote.property_type] || quote.property_type}
      <div className="eb-client-sub">{quote.lot_count ? `${quote.lot_count} lots` : (quote.surface ? `${quote.surface} m²` : '')}</div>
    </td>
    <td className="mono right">{formatEuro(quote.total_ttc)}</td>
    <td className="eb-rdv-cell" data-testid={`rdv-${quote.key}`}>{quote.rdv_libelle || '—'}</td>
    <td>
      {quote.rdv_status ? <span className={`eb-status ${quote.rdv_status === 'RDV confirmé' ? 'eb-status-ok' : 'eb-status-pending'}`} data-testid={`status-${quote.id}`}>{quote.rdv_status}</span> : quote.accepted_at
        ? <span className="eb-status eb-status-ok" data-testid={`status-${quote.id}`}>Accepté le {quote.accepted_at}</span>
        : <span className="eb-status eb-status-pending" data-testid={`status-${quote.id}`}>En attente</span>}
      {!quote.accepted_at && quote.last_reminder_at && (
        <div className="eb-client-sub">Relancé le {quote.last_reminder_at}{quote.reminder_count > 1 ? ` (×${quote.reminder_count})` : ''}</div>
      )}
      {quote.invoice_id && (
        <div className="eb-client-sub eb-invoice-hint" data-testid={`invoice-hint-${quote.id}`}>
          Facture {quote.invoice_id}
        </div>
      )}
      {quote.internal_notes && (
        <div className="eb-client-sub eb-note-hint" data-testid={`note-hint-${quote.id}`}>Note interne</div>
      )}
      {quote.funnel_step && <div className="eb-client-sub" data-testid={`funnel-step-${quote.id}`}>Parcours terminé</div>}
      {quote.email_sent === false && <div className="eb-error" data-testid={`email-failed-${quote.id}`}>E-mail non envoyé</div>}
    </td>
    <td className="right actions-cell">
      <a href={`/api/quotes/${encodeURIComponent(quote.id)}/pdf`} target="_blank" rel="noopener noreferrer"
        className="eb-link" data-testid={`pdf-link-${quote.id}`}>PDF</a>
      <button type="button" className="eb-btn-ghost eb-btn-small" onClick={() => onOpenDetails(quote)}
        data-testid={`details-btn-${quote.id}`}>Détails</button>
      {!quote.accepted_at && quote.rdv_status !== 'RDV confirmé' && quote.email && (
        <button type="button" className="eb-btn-ghost eb-btn-small" onClick={() => onRemind(quote.key)}
          disabled={reminding === quote.key} data-testid={`remind-btn-${quote.id}`}>
          {reminding === quote.key ? 'Envoi…' : 'Relancer'}
        </button>
      )}
      {quote.invoice_id ? (
        <button type="button" className="eb-btn-ghost eb-btn-small" onClick={() => onDownloadInvoice(quote)}
          data-testid={`invoice-download-btn-${quote.id}`}>Facture PDF</button>
      ) : (
        quote.email && (
          <button type="button" className="eb-btn-ghost eb-btn-small" onClick={() => onInvoice(quote)}
            disabled={invoicing === quote.key} data-testid={`invoice-btn-${quote.id}`}>
            {invoicing === quote.key ? 'Envoi…' : 'Facturer'}
          </button>
        )
      )}
    </td>
  </tr>
);

const DetailsPanel = ({ quote, onClose, token, onSaved, onRemind, reminding, onInvoice, invoicing, onDownloadInvoice }) => {
  const [notes, setNotes] = useState(quote.internal_notes || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/api/admin/quotes/notes`, { key: quote.key, notes }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedAt(res.data.internal_notes_updated_at);
      onSaved(quote.key, res.data.internal_notes, res.data.internal_notes_updated_at);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const history = quote.reminder_history || [];

  return (
    <div className="eb-modal-backdrop" onClick={onClose} data-testid="details-modal">
      <div className="eb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="eb-modal-head">
          <div>
            <h3 className="eb-modal-title">Devis {quote.id}</h3>
            <p className="eb-modal-sub">{quote.first_name} {quote.last_name} — {formatEuro(quote.total_ttc)}</p>
          </div>
          <button type="button" className="eb-modal-close" onClick={onClose} data-testid="details-close-btn" aria-label="Fermer">×</button>
        </div>

        <div className="eb-modal-body">
          <AdminBooking quote={quote} token={token} />
          <section className="eb-modal-section">
            <h4 className="eb-modal-section-title">Notes internes</h4>
            <textarea
              className="eb-textarea"
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={5000}
              placeholder="Contexte, échanges téléphoniques, points à suivre…"
              data-testid="notes-textarea"
            />
            <div className="eb-modal-row">
              <button type="button" className="eb-btn-primary eb-btn-small" onClick={save}
                disabled={saving} data-testid="save-notes-btn">
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              {savedAt && <span className="eb-modal-hint" data-testid="notes-saved-hint">Enregistré le {savedAt}</span>}
              {error && <span className="eb-error" data-testid="notes-error">{error}</span>}
            </div>
          </section>

          <section className="eb-modal-section">
            <h4 className="eb-modal-section-title">Historique des relances</h4>
            {history.length === 0 ? (
              <p className="eb-summary-empty" data-testid="history-empty">Aucune relance envoyée pour ce devis.</p>
            ) : (
              <ul className="eb-history-list" data-testid="history-list">
                {history.slice().reverse().map((h, idx) => (
                  <li key={idx} className="eb-history-item">
                    <span className="eb-history-date">{h.sent_at}</span>
                    <span className={`eb-history-tag ${h.automatic ? 'auto' : 'manuel'}`}>
                      {h.automatic ? 'Automatique' : 'Manuelle'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {!quote.accepted_at && quote.rdv_status !== 'RDV confirmé' && quote.email && (
              <button type="button" className="eb-btn-ghost eb-btn-small" onClick={() => onRemind(quote.key)}
                disabled={reminding === quote.key} data-testid="details-remind-btn">
                {reminding === quote.key ? 'Envoi…' : 'Envoyer une relance maintenant'}
              </button>
            )}
          </section>

          <section className="eb-modal-section">
            <h4 className="eb-modal-section-title">Facturation</h4>
            {quote.invoice_id ? (
              <div className="eb-invoice-info" data-testid="invoice-info">
                <p className="eb-modal-hint">
                  Facture <strong>{quote.invoice_id}</strong> envoyée le <strong>{quote.invoice_sent_at || '—'}</strong>.
                </p>
                <button type="button" className="eb-btn-primary eb-btn-small" onClick={() => onDownloadInvoice(quote)}
                  data-testid="details-invoice-download-btn">
                  Télécharger la facture (PDF)
                </button>
              </div>
            ) : (
              <>
                <p className="eb-summary-empty" data-testid="invoice-none">Aucune facture générée pour ce devis.</p>
                {quote.email && (
                  <button type="button" className="eb-btn-primary eb-btn-small" onClick={() => onInvoice(quote)}
                    disabled={invoicing === quote.key} data-testid="details-invoice-btn">
                    {invoicing === quote.key ? 'Envoi de la facture…' : 'Générer et envoyer la facture'}
                  </button>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default function AdminApp() {
  const [token, setToken] = useState(() => sessionStorage.getItem('eb_admin_token'));
  const [quotes, setQuotes] = useState(null);
  const [reminding, setReminding] = useState(null);
  const [invoicing, setInvoicing] = useState(null);
  const [notice, setNotice] = useState(null);
  const [filter, setFilter] = useState('tous');
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('quote') || '');
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [detailsKey, setDetailsKey] = useState(null);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState(search);
  const [pageInfo, setPageInfo] = useState({ total: 0, has_more: false });
  const [stats, setStats] = useState({ total: 0, accepted: 0, pending: 0, ca_accepted: 0, ca_pending: 0 });
  const [listLoading, setListLoading] = useState(false);
  const listRequest = useRef(null);
  useEffect(() => { const timer = setTimeout(() => setQuery(search), 250); return () => clearTimeout(timer); }, [search]);

  const logout = () => {
    sessionStorage.removeItem('eb_admin_token');
    setToken(null);
    setQuotes(null);
    setSelectedIds([]);
  };

  const load = useCallback(async (tok) => {
    listRequest.current?.abort();
    const controller = new AbortController();
    listRequest.current = controller;
    setListLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/quotes`, { headers: { Authorization: `Bearer ${tok}` }, signal: controller.signal,
        params: { offset: page * 100, limit: 100, search: query, status: filter === 'acceptes' ? 'accepted' : filter === 'attente' ? 'pending' : 'tous' } });
      if (controller.signal.aborted) return;
      setQuotes(res.data.quotes);
      setPageInfo({ total: res.data.total, has_more: res.data.has_more });
      setStats(res.data.stats);
      if (page > 0 && res.data.quotes.length === 0 && res.data.total > 0) setPage((value) => value - 1);
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err.response?.status === 401) logout();
      else setNotice({ type: 'error', text: 'Chargement des devis impossible' });
    } finally {
      if (!controller.signal.aborted) setListLoading(false);
    }
  }, [page, query, filter]);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const onLogin = (tok) => {
    sessionStorage.setItem('eb_admin_token', tok);
    setToken(tok);
  };

  const onRemind = async (quoteKey) => {
    setReminding(quoteKey);
    setNotice(null);
    try {
      const res = await axios.post(`${API}/api/admin/quotes/remind`, { key: quoteKey }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotice({ type: 'ok', text: res.data.message });
      await load(token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setNotice({ type: 'error', text: typeof detail === 'string' ? detail : "L'envoi de la relance a échoué" });
    } finally {
      setReminding(null);
    }
  };

  const toggleSelect = (key) => {
    setSelectedIds((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };

  const onInvoice = async (quote) => {
    const label = `${quote.first_name || ''} ${quote.last_name || ''}`.trim() || quote.id;
    const total = formatEuro(quote.total_ttc);
    const ok = window.confirm(
      `Générer et envoyer la facture au client ${label} (${quote.email}) ?\n` +
      `Devis : ${quote.id}\nMontant : ${total}\n\n` +
      `Une copie sera envoyée à contact@eb-energie.fr et gestion@eb-energie.fr.`
    );
    if (!ok) return;
    setInvoicing(quote.key);
    setNotice(null);
    try {
      const res = await axios.post(`${API}/api/admin/quotes/invoice`, { key: quote.key }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotice({ type: 'ok', text: res.data.message });
      await load(token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setNotice({ type: 'error', text: typeof detail === 'string' ? detail : "La génération de la facture a échoué" });
    } finally {
      setInvoicing(null);
    }
  };

  const onDownloadInvoice = async (quote) => {
    if (!quote.invoice_id) return;
    setNotice(null);
    try {
      const res = await axios.get(`${API}/api/admin/quotes/invoice/pdf`, {
        params: { key: quote.key },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture_eb_energie_${quote.invoice_id.replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      setNotice({ type: 'error', text: "Téléchargement impossible" });
    }
  };

  const filtered = (quotes || []).filter((q) => {
    if (filter === 'acceptes' && !q.accepted_at) return false;
    if (filter === 'attente' && q.accepted_at) return false;
    if (search) {
      const hay = `${q.id} ${q.first_name || ''} ${q.last_name || ''} ${q.email || ''} ${q.address || ''}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const visibleKeys = filtered.map((q) => q.key);
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selectedIds.includes(k));

  const toggleSelectAll = () => {
    setSelectedIds(allVisibleSelected ? selectedIds.filter((k) => !visibleKeys.includes(k)) : [...new Set([...selectedIds, ...visibleKeys])]);
  };

  const onDelete = async () => {
    if (selectedIds.length === 0) return;
    const ok = window.confirm(`Supprimer définitivement ${selectedIds.length} devis ? Cette action est irréversible.`);
    if (!ok) return;
    setDeleting(true);
    setNotice(null);
    try {
      const res = await axios.post(`${API}/api/admin/quotes/delete`, { keys: selectedIds }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotice({ type: 'ok', text: `${res.data.deleted} devis supprimé${res.data.deleted > 1 ? 's' : ''}` });
      setSelectedIds([]);
      await load(token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setNotice({ type: 'error', text: typeof detail === 'string' ? detail : 'La suppression a échoué' });
    } finally {
      setDeleting(false);
    }
  };

  const onNotesSaved = (key, notes, updatedAt) => {
    setQuotes((prev) => (prev || []).map((q) => (
      q.key === key ? { ...q, internal_notes: notes, internal_notes_updated_at: updatedAt } : q
    )));
  };

  const nbAcceptes = stats.accepted;
  const caAccepte = stats.ca_accepted;
  const caAttente = stats.ca_pending;
  const taux = stats.total ? Math.round((nbAcceptes / stats.total) * 100) : 0;

  const detailsQuote = detailsKey ? (quotes || []).find((q) => q.key === detailsKey) : null;

  return (
    <div className="eb-app">
      <header className="eb-header">
        <div className="eb-header-inner">
          <h1 className="eb-wordmark">EB <span>ENERGIE</span></h1>
          <p className="eb-header-sub">Suivi des devis — espace interne</p>
        </div>
        <div className="eb-progress"><div className="eb-progress-fill" style={{ width: '100%' }} /></div>
      </header>

      {!token ? (
        <AdminLogin onLogin={onLogin} />
      ) : (
        <main className="eb-main eb-admin-main">
          <nav className="eb-admin-nav" aria-label="Navigation du CRM">
            <a href="/admin" data-testid="admin-quotes-nav" aria-current={window.location.pathname === '/admin' ? 'page' : undefined}>Devis</a>
            <a href="/admin/parcours" data-testid="admin-funnel-nav" aria-current={window.location.pathname === '/admin/parcours' ? 'page' : undefined}>Parcours du simulateur</a>
          </nav>
          {window.location.pathname === '/admin/parcours' ? <AdminFunnel token={token} /> : <>
          <section className="eb-card eb-admin-card" data-testid="admin-dashboard">
            {quotes && (
              <div className="eb-stats-row">
                <StatCard label="Devis au total" value={stats.total} testid="stat-total" />
                <StatCard label="En attente" value={stats.pending} testid="stat-attente" />
                <StatCard label="Acceptés" value={`${nbAcceptes} (${taux} %)`} testid="stat-acceptes" />
                <StatCard label="CA accepté TTC" value={formatEuro(caAccepte)} testid="stat-ca-accepte" />
                <StatCard label="CA en attente TTC" value={formatEuro(caAttente)} testid="stat-ca-attente" />
              </div>
            )}

            <div className="eb-admin-toolbar">
              <input
                type="search"
                className="eb-input eb-search"
                placeholder="Rechercher : référence, nom, email, adresse…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); setSelectedIds([]); }}
                data-testid="admin-search-input"
              />
              <div className="eb-admin-actions">
                <div className="eb-chips eb-chips-3" style={{ minWidth: 300 }}>
                  {[['tous', 'Tous'], ['attente', 'En attente'], ['acceptes', 'Acceptés']].map(([id, label]) => (
                    <button key={id} type="button" className={`eb-chip ${filter === id ? 'selected' : ''}`}
                      onClick={() => { setFilter(id); setPage(0); setSelectedIds([]); }} data-testid={`filter-${id}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <button type="button" className="eb-btn-danger eb-btn-small" onClick={onDelete}
                  disabled={selectedIds.length === 0 || deleting} data-testid="delete-selected-btn">
                  {deleting ? 'Suppression…' : `Supprimer${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
                </button>
                <button type="button" className="eb-btn-ghost eb-btn-small" onClick={logout} data-testid="admin-logout-btn">
                  Déconnexion
                </button>
              </div>
            </div>

            {notice && (
              <p className={notice.type === 'ok' ? 'eb-notice-ok' : 'eb-error'} data-testid="admin-notice">{notice.text}</p>
            )}

            {!quotes ? (
              <div className="eb-loading"><div className="eb-spinner" /><p>Chargement des devis…</p></div>
            ) : (
              <div className="eb-table-wrap">
                <table className="eb-table" data-testid="quotes-table">
                  <thead>
                    <tr>
                      <th className="check-cell">
                        <input type="checkbox" className="eb-checkbox" checked={allVisibleSelected} onChange={toggleSelectAll}
                          data-testid="select-all-checkbox" aria-label="Tout sélectionner" />
                      </th>
                      <th>Réf</th><th>Date</th><th>Client</th><th>Bien</th>
                      <th className="right">Total TTC</th><th data-testid="admin-rdv-column">RDV</th><th data-testid="admin-status-column">Statut</th><th className="right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((q) => (
                      <QuoteRow key={q.key} quote={q} onRemind={onRemind} reminding={reminding}
                        checked={selectedIds.includes(q.key)} onToggle={toggleSelect}
                        onOpenDetails={(qq) => setDetailsKey(qq.key)}
                        onInvoice={onInvoice} invoicing={invoicing}
                        onDownloadInvoice={onDownloadInvoice} />
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && <p className="eb-summary-empty" data-testid="admin-no-quotes" style={{ padding: 16 }}>Aucun devis dans cette vue.</p>}
              </div>
            )}
            {quotes && <nav className="eb-admin-pagination" aria-label="Pages des devis" data-testid="admin-pagination">
              <span data-testid="admin-page-range">{quotes.length ? `${page * 100 + 1}–${page * 100 + quotes.length}` : '0'} sur {pageInfo.total}</span>
              <button type="button" className="eb-btn-ghost" data-testid="admin-page-previous" disabled={page === 0 || listLoading} onClick={() => { setPage((value) => value - 1); setSelectedIds([]); }}>Précédent</button>
              <button type="button" className="eb-btn-ghost" data-testid="admin-page-next" disabled={!pageInfo.has_more || listLoading} onClick={() => { setPage((value) => value + 1); setSelectedIds([]); }}>Suivant</button>
            </nav>}
          </section>
          </>}
        </main>
      )}

      {detailsQuote && (
        <DetailsPanel
          quote={detailsQuote}
          onClose={() => setDetailsKey(null)}
          token={token}
          onSaved={onNotesSaved}
          onRemind={onRemind}
          reminding={reminding}
          onInvoice={onInvoice}
          invoicing={invoicing}
          onDownloadInvoice={onDownloadInvoice}
        />
      )}
    </div>
  );
}
